import React, { useState, useEffect, useMemo } from 'react';
import './WorkspaceSelectionModal.css';

export const WorkspaceSelectionModal = ({
  isOpen,
  workspaces = [],
  currentCentreId = null,
  currentRoleId = null,
  onSelect,
  onClose,
  title = "Select Operating Workspace",
  subtitle = "Your account is authorized for multiple diagnostic centers or roles. Choose your active workspace to continue."
}) => {
  const [selectedCentreId, setSelectedCentreId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Group unique authorized centers from workspaces
  const availableCentres = useMemo(() => {
    const map = new Map();
    workspaces.forEach((w) => {
      if (!map.has(w.centreId)) {
        map.set(w.centreId, {
          centreId: w.centreId,
          centreName: w.centreName,
          centreCode: w.centreCode,
          isDefault: w.isDefault
        });
      }
    });
    return Array.from(map.values());
  }, [workspaces]);

  // Filter available roles belonging to selected centre
  const availableRolesForCentre = useMemo(() => {
    if (!selectedCentreId) return [];
    return workspaces.filter((w) => w.centreId === parseInt(selectedCentreId, 10));
  }, [workspaces, selectedCentreId]);

  // Initialize or reset selections when modal opens or centre changes
  useEffect(() => {
    if (isOpen && workspaces.length > 0) {
      // Pick current centre or default centre or first centre
      const initialCentre = currentCentreId
        ? availableCentres.find((c) => c.centreId === currentCentreId)
        : (availableCentres.find((c) => c.isDefault) || availableCentres[0]);

      if (initialCentre) {
        setSelectedCentreId(initialCentre.centreId.toString());
        const rolesForInitial = workspaces.filter((w) => w.centreId === initialCentre.centreId);
        const initialRole = currentRoleId
          ? rolesForInitial.find((r) => r.roleId === currentRoleId)
          : (rolesForInitial.find((r) => r.isDefault) || rolesForInitial[0]);

        if (initialRole) {
          setSelectedRoleId(initialRole.roleId.toString());
        }
      }
      setError(null);
    }
  }, [isOpen, workspaces, currentCentreId, currentRoleId, availableCentres]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && onClose && !isSubmitting) {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, isSubmitting]);

  // When selected centre changes, adjust role selection
  const handleCentreChange = (e) => {
    const newCentreId = e.target.value;
    setSelectedCentreId(newCentreId);

    const rolesForNew = workspaces.filter((w) => w.centreId === parseInt(newCentreId, 10));
    if (rolesForNew.length > 0) {
      const defRole = rolesForNew.find((r) => r.isDefault) || rolesForNew[0];
      setSelectedRoleId(defRole.roleId.toString());
    } else {
      setSelectedRoleId('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCentreId || !selectedRoleId) {
      setError("Please select both an authorized Center and Role.");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const centreId = parseInt(selectedCentreId, 10);
      const roleId = parseInt(selectedRoleId, 10);
      await onSelect(centreId, roleId);
    } catch (err) {
      setError(err.message || "Failed to establish workspace context.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="ws-modal-overlay workspace-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ws-modal-heading"
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose && !isSubmitting) {
          onClose();
        }
      }}
    >
      <div className="ws-modal-card workspace-modal-card">
        {/* Top Accent Gradient Line */}
        <div className="ws-modal-top-accent" aria-hidden="true" />

        {/* Modal Header */}
        <div className="ws-modal-header">
          <div className="ws-header-icon-box" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="3" />
              <path d="M12 8v8" />
              <path d="M8 12h8" />
            </svg>
          </div>
          <div className="ws-header-text">
            <h2 id="ws-modal-heading" className="ws-modal-title">{title}</h2>
            <p className="ws-modal-subtitle">{subtitle}</p>
          </div>
          {onClose && (
            <button
              type="button"
              className="ws-close-btn workspace-close-btn"
              onClick={onClose}
              aria-label="Close modal"
              title="Close"
              disabled={isSubmitting}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {/* Error Notification */}
        {error && (
          <div className="ws-error-banner workspace-error-banner" role="alert">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* Main Form */}
        <form onSubmit={handleSubmit} className="ws-form workspace-form">
          {/* 1. Operating Center */}
          <div className="ws-field-group workspace-field-group">
            <div className="ws-field-label-row">
              <label className="ws-field-label" htmlFor="ws-center-select">
                <span className="ws-field-label-icon" aria-hidden="true">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 21h18M4 18h16M6 18v-7M10 18v-7M14 18v-7M18 18v-7M2 11h20M12 3L2 11h20L12 3z" />
                  </svg>
                </span>
                <span>Operating Center</span>
              </label>
              {availableCentres.length > 1 && (
                <span className="ws-count-badge">
                  {availableCentres.length} Centers
                </span>
              )}
            </div>

            <div className="ws-select-container">
              <div className="ws-select-leading-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 21h18" />
                  <path d="M5 21V7l8-4v18" />
                  <path d="M19 21V11l-6-4" />
                  <line x1="9" y1="9" x2="9.01" y2="9" />
                  <line x1="9" y1="13" x2="9.01" y2="13" />
                  <line x1="9" y1="17" x2="9.01" y2="17" />
                </svg>
              </div>

              <select
                id="ws-center-select"
                className="ws-select-input workspace-select"
                value={selectedCentreId}
                onChange={handleCentreChange}
                disabled={isSubmitting || availableCentres.length === 0}
              >
                {availableCentres.map((c) => (
                  <option key={c.centreId} value={c.centreId}>
                    {c.centreName} {c.centreCode ? `(${c.centreCode})` : ''} {c.isDefault ? '• [Default]' : ''}
                  </option>
                ))}
              </select>

              <div className="ws-select-chevron" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>

            <p className="ws-field-hint">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <span>Only diagnostic centers assigned to your employee account are listed.</span>
            </p>
          </div>

          {/* 2. Assigned Role */}
          <div className="ws-field-group workspace-field-group">
            <div className="ws-field-label-row">
              <label className="ws-field-label" htmlFor="ws-role-select">
                <span className="ws-field-label-icon" aria-hidden="true">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                </span>
                <span>Assigned Role for Selected Center</span>
              </label>
              {availableRolesForCentre.length > 1 && (
                <span className="ws-count-badge">
                  {availableRolesForCentre.length} Roles
                </span>
              )}
            </div>

            <div className="ws-select-container">
              <div className="ws-select-leading-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>

              <select
                id="ws-role-select"
                className="ws-select-input workspace-select"
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                disabled={isSubmitting || availableRolesForCentre.length === 0}
              >
                {availableRolesForCentre.map((r) => (
                  <option key={r.roleId} value={r.roleId}>
                    {r.roleName ? r.roleName.toUpperCase() : ''} {r.isDefault ? '• [Default]' : ''}
                  </option>
                ))}
              </select>

              <div className="ws-select-chevron" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>

            <p className="ws-field-hint">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <span>Permissions are strictly scoped to the chosen Center and Role.</span>
            </p>
          </div>

          {/* Modal Footer Actions */}
          <div className="ws-modal-footer workspace-modal-footer">
            {onClose && (
              <button
                type="button"
                className="ws-btn ws-btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="ws-btn ws-btn-primary workspace-submit-btn"
              disabled={isSubmitting || !selectedCentreId || !selectedRoleId}
            >
              {isSubmitting ? (
                <>
                  <span className="ws-btn-spinner" aria-hidden="true" />
                  <span>Establishing Workspace...</span>
                </>
              ) : (
                <>
                  <span>Continue to Workspace</span>
                  <svg className="ws-btn-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkspaceSelectionModal;
